import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Rocket, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LAUNCH_DATE, pad, useCountdown, useLaunchDismissed } from "@/lib/launch";

const USING_WYLO = [
  { to: "/individuals", label: "Individuals", desc: "Who WYLO is for, and the research behind it" },
  { to: "/enterprise", label: "Enterprise", desc: "Roll-up insights, reporting and dedicated support" },
  { to: "/charities", label: "Charities", desc: "Inclusion, autonomy and independence at work" },
];

function UsingWyloMenu() {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const pinned = useRef(false);
  const wrap = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const active = location.pathname === "/users" || USING_WYLO.some((c) => location.pathname.startsWith(c.to));
  const show = () => { clearTimeout(timer.current); setOpen(true); };
  const hide = () => { if (pinned.current) return; timer.current = setTimeout(() => setOpen(false), 120); };
  const closeAll = () => { pinned.current = false; setOpen(false); };
  const toggle = (e) => {
    e.stopPropagation();
    clearTimeout(timer.current);
    if (open && pinned.current) return closeAll();
    pinned.current = true;
    setOpen(true);
  };
  useEffect(() => {
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) closeAll(); };
    document.addEventListener("pointerdown", onDoc);
    return () => document.removeEventListener("pointerdown", onDoc);
  }, []);
  useEffect(() => { closeAll(); }, [location.pathname]);
  return (
    <div className="relative" ref={wrap} onMouseEnter={show} onMouseLeave={hide}>
      <div className={`inline-flex items-center gap-1 text-sm transition-colors ${active ? "text-[#1A3A5C] font-semibold" : "text-[#555568] hover:text-[#1A1A2E]"}`}>
        <button type="button" onClick={() => { closeAll(); navigate("/users"); }} data-testid="nav-link-users">Users</button>
        <button type="button" onClick={toggle} aria-haspopup="menu" aria-expanded={open} aria-label="Open Users menu" data-testid="nav-users-chevron">
          <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (
        <div role="menu" data-testid="using-wylo-menu"
          className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-72">
          <div className="rounded-xl border border-[#E8ECF2] bg-white shadow-[0_20px_50px_rgba(26,58,92,0.15)] p-2">
            <NavLink to="/users" role="menuitem" onClick={closeAll} data-testid="nav-sub-all-users"
              className="block rounded-lg px-3 py-2 hover:bg-[#F8F9FC] text-[10px] uppercase tracking-[0.18em] text-[#2C6BAC] font-semibold">
              Who is WYLO for?
            </NavLink>
            {USING_WYLO.map((c) => (
              <NavLink key={c.to} to={c.to} role="menuitem" onClick={closeAll}
                data-testid={`nav-sub-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `block rounded-lg px-3 py-2.5 hover:bg-[#F8F9FC] ${isActive ? "bg-[#F8F9FC]" : ""}`}>
                <p className="text-sm font-semibold text-[#1A1A2E]">{c.label}</p>
                <p className="text-xs text-[#555568]">{c.desc}</p>
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HeaderCountdown({ compact }) {
  const { days, hours, minutes, seconds } = useCountdown(LAUNCH_DATE);
  return (
    <Link
      to="/register"
      data-testid="header-countdown"
      className={`inline-flex items-center gap-2 rounded-full border border-[#1A3A5C]/20 bg-white/70 backdrop-blur px-3 py-1.5 hover:border-[#2C6BAC] transition-colors ${compact ? "" : ""}`}
      title="WYLO goes live on 1 November 2026"
    >
      <Rocket size={12} className="text-[#2C6BAC]" />
      <span className="text-[10px] uppercase tracking-[0.18em] text-[#2C6BAC] font-semibold">Live in</span>
      <span className="font-mono text-xs text-[#1A1A2E] tabular-nums">
        {days}d {pad(hours)}:{pad(minutes)}:<span key={seconds} className="inline-block animate-[cd-pulse-dark_1s_ease-out] text-[#1A3A5C]">{pad(seconds)}</span>
      </span>
    </Link>
  );
}

export default function MarketingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const launchDismissed = useLaunchDismissed();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", label: "Home" },
    { to: "/our-story", label: "Our Story" },
    { label: "Users", children: USING_WYLO },
    { to: "/resources", label: "Resources" },
    { to: "/integrations", label: "Integrations" },
    { to: "/pricing", label: "Pricing" },
  ];

  return (
    <header
      data-testid="marketing-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all ${
        scrolled
          ? "backdrop-blur-md bg-[#F8F9FC]/85 border-b border-[#E8ECF2]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <div className="h-8 w-8 rounded-md bg-[#1A3A5C] flex items-center justify-center">
            <span className="font-display font-bold text-[#F8F9FC] text-sm">W</span>
          </div>
          <span className="font-display text-xl font-bold tracking-tighter text-[#1A1A2E]">
            wylo
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {links.map((l) => l.children ? <UsingWyloMenu key={l.label} /> : (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm transition-colors ${
                  isActive
                    ? "text-[#1A3A5C] font-semibold"
                    : "text-[#555568] hover:text-[#1A1A2E]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block relative">
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="nav-login-btn">
              <Button variant="ghost" className="text-[#1A1A2E] hover:bg-[#E8ECF2]">
                Log in
              </Button>
            </Link>
            <Link to="/register" data-testid="nav-trial-btn">
              <Button className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white relative overflow-hidden btn-shimmer">
                Start Free Trial
              </Button>
            </Link>
          </div>
          {launchDismissed && (
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 whitespace-nowrap">
              <HeaderCountdown />
            </div>
          )}
        </div>

        <button
          className="md:hidden p-2 rounded-md hover:bg-[#E8ECF2]"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#E8ECF2] bg-[#F8F9FC]">
          <div className="px-6 py-4 flex flex-col gap-3">
            {launchDismissed && <div className="pb-2"><HeaderCountdown compact /></div>}
            {links.map((l) => l.children ? (
              <div key={l.label}>
                <NavLink to="/users" className={({ isActive }) => `text-xs uppercase tracking-[0.18em] font-semibold ${isActive ? "text-[#1A3A5C]" : "text-[#555568]"}`} data-testid="nav-mobile-users">Users</NavLink>
                <div className="mt-2 flex flex-col gap-2 pl-3 border-l border-[#E8ECF2]">
                  {l.children.map((c) => (
                    <NavLink key={c.to} to={c.to} className={({ isActive }) => `text-base ${isActive ? "text-[#1A3A5C] font-semibold" : "text-[#555568]"}`}>
                      {c.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-[#1A3A5C] font-semibold" : "text-[#555568]"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex gap-2 pt-3 border-t border-[#E8ECF2]">
              <Link to="/login" className="flex-1">
                <Button variant="outline" className="w-full">Log in</Button>
              </Link>
              <Link to="/register" className="flex-1">
                <Button className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white w-full">
                  Start Trial
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
